import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { FaEye, FaTimes } from 'react-icons/fa';
import RFQDashboard from "./rfqdashboard";
import jrslogo from '../images/jrslogo.png';
import dayjs from 'dayjs';
import LogoConnections from './logocollab';

const API_URL = '/api';
// Chart data for superadmin/vendor admin
const adminAreaData = [
  { name: 'Jan', value: 400 },
  { name: 'Feb', value: 300 },
  { name: 'Mar', value: 200 },
  { name: 'Apr', value: 278 },
  { name: 'May', value: 189 },
  { name: 'Jun', value: 239 },
  { name: 'Jul', value: 349 }
];
const adminLineData = [
  { name: 'Mon', value: 120 },
  { name: 'Tue', value: 210 },
  { name: 'Wed', value: 80 },
  { name: 'Thu', value: 160 },
  { name: 'Fri', value: 250 },
];
const adminPieData = [
  { name: 'A', value: 400 },
  { name: 'B', value: 300 },
  { name: 'C', value: 300 },
  { name: 'D', value: 200 },
];
const adminPieColors = ['#2b6777', '#52ab98', '#c8d8e4', '#f2f2f2'];

// Sample PR and PO data for admin
const prData = [
  { prNo: 'PR-001', prDate: '2024-01-15', prStatus: 'Approved', submissionDate: '2024-01-10' },
  { prNo: 'PR-002', prDate: '2024-01-20', prStatus: 'Pending', submissionDate: '2024-01-18' },
  { prNo: 'PR-003', prDate: '2024-01-25', prStatus: 'Rejected', submissionDate: '2024-01-22' },
  { prNo: 'PR-004', prDate: '2024-01-30', prStatus: 'Approved', submissionDate: '2024-01-28' },
  { prNo: 'PR-005', prDate: '2024-02-05', prStatus: 'Pending', submissionDate: '2024-02-01' },
];
const poData = [
  { pon: 'PO-001', poDate: '2024-01-20', currentStatus: 'Active', shippedStatus: 'Shipped', receptStatus: 'Received', overdueStatus: 'On Time', due: '2024-02-15' },
  { pon: 'PO-002', poDate: '2024-01-25', currentStatus: 'Active', shippedStatus: 'In Transit', receptStatus: 'Pending', overdueStatus: 'On Time', due: '2024-02-20' },
  { pon: 'PO-003', poDate: '2024-01-30', currentStatus: 'Completed', shippedStatus: 'Delivered', receptStatus: 'Received', overdueStatus: 'Overdue', due: '2024-02-10' },
  { pon: 'PO-004', poDate: '2024-02-05', currentStatus: 'Active', shippedStatus: 'Pending', receptStatus: 'Pending', overdueStatus: 'On Time', due: '2024-03-01' },
  { pon: 'PO-005', poDate: '2024-02-10', currentStatus: 'Cancelled', shippedStatus: 'Cancelled', receptStatus: 'Cancelled', overdueStatus: 'Cancelled', due: '2024-03-05' },
];

const DefaultBuyerLogo = () => (
  <div className="w-10 h-10 rounded-full bg-[#2b6777] flex items-center justify-center text-white font-bold text-sm">
    B
  </div>
);
const DefaultClientLogo = () => (
  <div className="w-12 h-12 rounded-full bg-[#52ab98] flex items-center justify-center text-white font-bold text-sm">
    C
  </div>
);
const DefaultJRSLogo = () => (
  <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-white shadow-md">
    <img src={jrslogo} alt="JRS Logo" className="w-full h-full object-contain" />
  </div>
);


// Logo Image Component (unchanged)
const LogoImage = ({ src, alt, fallback, isJRS = false }) => {
  const [imgError, setImgError] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    if (isJRS) {
      setImgSrc('/images/jrslogo.png');
      setIsValidating(false);
      return;
    }
    if (!src) {
      setImgError(true);
      setIsValidating(false);
      return;
    }
    if (src.startsWith('data:image')) {
      setImgSrc(src);
      setIsValidating(false);
    } else if (src.startsWith('/')) {
      const fullPath = `${API_URL}${src}`;
      validateImage(fullPath, () => {
        const altPath = `${API_URL}/uploads${src}`;
        validateImage(altPath, () => {
          setImgError(true);
          setIsValidating(false);
        });
      });
    } else {
      setImgError(true);
      setIsValidating(false);
    }
  }, [src, isJRS]);

  const validateImage = (url, onFailure) => {
    const img = new Image();
    img.onload = () => {
      setImgSrc(url);
      setIsValidating(false);
    };
    img.onerror = () => {
      if (onFailure) onFailure();
      else {
        setImgError(true);
        setIsValidating(false);
      }
    };
    img.src = url;
  };

  if (isValidating) {
    return (
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
        <div className="w-5 h-5 border-t-2 border-[#2b6777] border-solid rounded-full animate-spin"></div>
      </div>
    );
  }
  if (imgError || (!src && !isJRS)) return fallback;

  return (
    <img
      src={imgSrc}
      alt={alt}
      className="w-10 h-10 rounded-full border-2 border-[#2b6777] object-cover"
      onError={() => setImgError(true)}
    />
  );
};


const Dashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [clientOnboarding, setClientOnboarding] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rfqLoading, setRfqLoading] = useState(true);
  const [logosLoading, setLogosLoading] = useState(true);
  const [showPRModal, setShowPRModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [logos, setLogos] = useState({
    buyerLogo: '',
    clientLogo: '',
    jrsLogo: '/images/jrslogo.png'
  });
  const [logoError, setLogoError] = useState(null);
  
  // Initialize with empty data
  const [areaData, setAreaData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const token = localStorage.getItem('authToken');
  
  // Generate chart data from RFQs
  const generateChartData = (rfqs) => {
    console.log("Generating chart data from RFQs:", rfqs);
    
    // Generate area chart data (RFQs by month)
    const monthlyData = {};
    rfqs.forEach(rfq => {
      if (rfq.rfq_date) {
        const month = dayjs(rfq.rfq_date).format('MMM');
        monthlyData[month] = (monthlyData[month] || 0) + 1;
      }
    });
    
    const areaData = Object.keys(monthlyData).map(month => ({
      name: month,
      rfqs: monthlyData[month]
    }));
    
    console.log("Generated area data:", areaData);
    
    // Generate line chart data (RFQs by day of week)
    const weeklyData = {
      'Mon': 0, 'Tue': 0, 'Wed': 0, 'Thu': 0, 'Fri': 0, 'Sat': 0, 'Sun': 0
    };
    
    rfqs.forEach(rfq => {
      if (rfq.rfq_date) {
        const dayOfWeek = dayjs(rfq.rfq_date).format('ddd');
        if (weeklyData.hasOwnProperty(dayOfWeek)) {
          weeklyData[dayOfWeek]++;
        }
      }
    });
    
    const lineData = Object.keys(weeklyData).map(day => ({
      name: day,
      rfqs: weeklyData[day]
    }));
    
    console.log("Generated line data:", lineData);
    
    // Generate pie chart data (RFQs by status)
    const statusData = {};
    rfqs.forEach(rfq => {
      const status = rfq.status || 'Unknown';
      statusData[status] = (statusData[status] || 0) + 1;
    });
    
    const pieData = Object.keys(statusData).map(status => ({
      name: status,
      value: statusData[status]
    }));
    
    console.log("Generated pie data:", pieData);
    
    return { areaData, lineData, pieData };
  };
  
  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${API_URL}/current-user`);
        const data = await response.json();
        if (data.user) {
          setCurrentUser(data.user);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCurrentUser();
  }, [navigate]);
  
  // Fetch RFQ data for the logged-in user
  useEffect(() => {
    const fetchUserRFQs = async () => {
      if (!currentUser) return;
      
      setRfqLoading(true);
      try {
        console.log(`=== FETCHING RFQS FOR USER ===`);
        console.log(`User: ${currentUser.Username}, UserID: ${currentUser.UserID}, Role: ${currentUser.Role}`);
        
        // For admin roles, use sample data
        if (currentUser.Role === "Superadmin" || currentUser.Role === "Vendoradmin") {
          console.log("Setting admin chart data");
          setAreaData(adminAreaData);
          setLineData(adminLineData);
          setPieData(adminPieData);
          setRfqs([]);
          setRfqLoading(false);
          return;
        }
        
        // For vendor users, fetch and filter RFQs
        console.log("=== FETCHING RFQS ===");
        const rfqRes = await fetch(`${API_URL}/rfqs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let rfqData = await rfqRes.json();
        console.log("Total RFQs fetched:", rfqData.length);
        
        // Log all RFQs with their vendor codes
        console.log("=== ALL RFQS ===");
        rfqData.forEach(rfq => {
          console.log(`RFQ ID: ${rfq.id}, RFQ No: ${rfq.rfq_no}, Vendor Code: ${rfq.vendor_code}`);
        });
        
        // Process RFQs based on user role
        if (currentUser.Role === "Vendoruser") {
          // For vendor users, get client onboarding data and filter RFQs
          console.log("=== FETCHING CLIENT FOR VENDOR USER ===");
          console.log("Fetching for UserID:", currentUser.UserID);
          
          // Try to get all client onboarding data first to see what exists
          console.log("=== FETCHING ALL CLIENTS ===");
          const allClientsRes = await fetch(`${API_URL}/clients`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const allClientsData = await allClientsRes.json();
          console.log("All clients data:", allClientsData);
          setAllClients(allClientsData);
          
          // Get client onboarding data for this specific user
          const clientRes = await fetch(`${API_URL}/clients?user_id=${currentUser.UserID}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const clientData = await clientRes.json();
          console.log("Client onboarding response for user:", clientData);
          
          let clientInfo = null;
          if (clientData && clientData.length > 0) {
            // Check if any of the client records match the user's ID
            const matchingClient = clientData.find(client => 
              String(client.user_id) === String(currentUser.UserID)
            );
            
            if (matchingClient) {
              clientInfo = matchingClient;
              setClientOnboarding(clientInfo);
              console.log("=== MATCHING CLIENT FOUND ===");
              console.log("Client Info:", clientInfo);
              console.log("Client user_id:", clientInfo.user_id);
              console.log("Client vendor_code:", clientInfo.vendor_code);
            } else {
              console.log("=== NO MATCHING CLIENT FOUND ===");
              console.log("Client data exists but none match user ID:", currentUser.UserID);
              console.log("Available client IDs:", clientData.map(c => c.user_id));
            }
          } else {
            console.log("=== NO CLIENT DATA FOUND FOR USER ===");
            console.log("No client onboarding records found for user:", currentUser.UserID);
          }
          
          // Filter RFQs for vendor user
          if (clientInfo && clientInfo.vendor_code) {
            console.log("=== FILTERING RFQS FOR VENDOR USER ===");
            console.log("Filtering for vendor_code:", clientInfo.vendor_code);
            
            rfqData = rfqData.filter(rfq => {
              const matches = String(rfq.vendor_code) === String(clientInfo.vendor_code);
              console.log(`RFQ ${rfq.rfq_no} (vendor_code: ${rfq.vendor_code}) matches ${clientInfo.vendor_code}: ${matches}`);
              return matches;
            });
            
            console.log("=== FILTERED RFQs ===");
            console.log(`RFQs after filtering: ${rfqData.length}`);
            rfqData.forEach(rfq => {
              console.log(`RFQ ID: ${rfq.id}, RFQ No: ${rfq.rfq_no}, Vendor Code: ${rfq.vendor_code}`);
            });
          } else {
            console.log("=== NO VENDOR CODE FOUND FOR VENDOR USER ===");
            console.log("Vendor user has no client info or vendor_code");
            rfqData = [];
          }
        }
        
        setRfqs(rfqData);
        
        // Generate chart data for non-admin users
        if (currentUser.Role !== "Superadmin" && currentUser.Role !== "Vendoradmin") {
          const { areaData, lineData, pieData } = generateChartData(rfqData);
          setAreaData(areaData);
          setLineData(lineData);
          setPieData(pieData);
        }
        
      } catch (error) {
        console.error("=== ERROR ===");
        console.error("Error fetching RFQs:", error);
        setRfqs([]);
      } finally {
        setRfqLoading(false);
      }
    };
    
    if (currentUser) {
      fetchUserRFQs();
    }
  }, [currentUser, token]);
  // Fetch Vendor Admin Logo (static for all users)
useEffect(() => {
  const fetchVendorAdminLogo = async () => {
    if (!currentUser) return;

    try {
      const response = await fetch(`${API_URL}/vendoradmin/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      setLogos(prev => ({
        ...prev,
        vendorAdminLogo: data.success ? data.logo || "" : "",
        vendorAdminName: data.success ? data.name || "" : "",
      }));
    } catch (err) {
      console.error("Error fetching Vendor Admin logo:", err);
    }
  };

  fetchVendorAdminLogo();
}, [currentUser, token]);

useEffect(() => {
  const fetchVendorUserLogo = async () => {
    if (!currentUser || currentUser.Role !== "Vendoruser") return;

    try {
      const res = await fetch(`${API_URL}/vendoruser/logo?userId=${currentUser.UserID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        setLogos(prev => ({
          ...prev,
          clientLogo: data.buyerLogo || "", // <-- use buyerLogo from API
        }));
      } else {
        console.warn("Vendoruser logo fetch returned success=false", data);
      }
    } catch (err) {
      console.error("Error fetching Vendoruser logo:", err);
    }
  };

  fetchVendorUserLogo();
}, [currentUser, token]);

  
  const handleLogout = () => {
    navigate('/login');
  };
  
  const handlePRClick = (pr) => {
    setSelectedPR(pr);
    setShowPRModal(true);
  };
  
  const handlePOClick = (po) => {
    setSelectedPO(po);
    setShowPOModal(true);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
      case 'Active':
      case 'Shipped':
      case 'Delivered':
      case 'Received':
      case 'On Time':
      case 'Win':
        return 'bg-green-100 text-green-800';
      case 'Pending':
      case 'In Transit':
      case 'Negotiation':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
      case 'Cancelled':
      case 'Overdue':
      case 'Lost':
        return 'bg-red-100 text-red-800';
      case 'Bid Closed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Calculate RFQ status counts
  const getRFQStatusCounts = () => {
    const counts = {
      open: 0,
      negotiation: 0,
      bidClosed: 0,
      win: 0,
      lost: 0
    };
    
    rfqs.forEach(rfq => {
      switch (rfq.status) {
        case 'Open':
          counts.open++;
          break;
        case 'Negotiation':
          counts.negotiation++;
          break;
        case 'Bid Closed':
          counts.bidClosed++;
          break;
        case 'Win':
          counts.win++;
          break;
        case 'Lost':
          counts.lost++;
          break;
        default:
          break;
      }
    });
    
    return counts;
  };
  
  const statusCounts = getRFQStatusCounts();
  
  // Admin stats
  const adminStats = [
    { label: 'Total PRs', value: prData.length, color: 'from-[#2b6777] to-[#52ab98]' },
    { label: 'Total POs', value: poData.length, color: 'from-[#52ab98] to-[#c8d8e4]' },
    { label: 'Active POs', value: poData.filter(po => po.currentStatus === 'Active').length, color: 'from-[#c8d8e4] to-[#f2f2f2]' },
    { label: 'Pending PRs', value: prData.filter(pr => pr.prStatus === 'Pending').length, color: 'from-[#f2f2f2] to-[#2b6777]' },
  ];
  
  // Vendor user stats
  const vendorStats = [
    { label: 'Open', value: statusCounts.open, color: 'from-[#2b6777] to-[#52ab98]' },
    { label: 'Negotiation', value: statusCounts.negotiation, color: 'from-[#52ab98] to-[#c8d8e4]' },
    { label: 'Bid Closed', value: statusCounts.bidClosed, color: 'from-[#c8d8e4] to-[#f2f2f2]' },
    { label: 'Win', value: statusCounts.win, color: 'from-[#f2f2f2] to-[#2b6777]' },
    { label: 'Lost', value: statusCounts.lost, color: 'from-[#2b6777] to-[#52ab98]' },
  ];
  
  // Pie chart colors
  const pieColors = ['#2b6777', '#52ab98', '#c8d8e4', '#f2f2f2', '#ff6b6b', '#4ecdc4'];
  
  if (loading) {
    return (
      <div className="min-h-screen flex bg-[#E5E1DA]">
        <Sidebar onLogout={handleLogout} />
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="flex items-center justify-center h-full">
            <div className="text-[#2b6777] font-semibold">Loading...</div>
          </div>
        </main>
      </div>
    );
  }
  
  if (!currentUser) {
    return null;
  }
  
  // Helper for greeting name
  const getGreetingName = (user) => {
    return user?.FullName || user?.Username || user?.Name || user?.name || user?.email || 'User';
  };
  
  // Determine if user is admin or vendor user
  const isAdmin = currentUser.Role === "Superadmin" || currentUser.Role === "Vendoradmin";
  const stats = isAdmin ? adminStats : vendorStats;
  
  return (
    <div className="min-h-screen flex bg-[#E5E1DA]">
      {/* Sidebar (collapsible on mobile via ml-0/md:ml-16) */}
      <Sidebar onLogout={handleLogout} />
      <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-hidden 
                       ml-0 md:ml-16 transition-all duration-300 relative">
        {/* User Info Card */}
        <div className="flex flex-col md:flex-row items-center md:items-start 
                        justify-between mb-6 md:mb-8 animate-fade-in gap-4 md:gap-6">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full 
                            bg-gradient-to-br from-[#2b6777] to-[#52ab98] 
                            flex items-center justify-center shadow-lg">
              <span className="text-2xl md:text-4xl text-white font-bold">
                {getGreetingName(currentUser)[0]?.toUpperCase()}
              </span>
            </div>
            <div className="text-center md:text-left">

              <div className='w-full flex flex-col md:flex-row md:items-center md:gap-6 justify-between'>
                <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2b6777] mb-1">
                Welcome, {getGreetingName(currentUser)}!
              </h1></div>
        
<div className="absolute top-2 right-4">
   <LogoConnections
  role={currentUser.Role}
  buyerLogo={logos.vendorAdminLogo}   // Vendoradmin logo
  clientLogo={logos.clientLogo ?? ''} // Vendoruser logo

  fallbackName={logos.vendorAdminName}
/>

</div>




                
              </div>
              


             


              <div className="flex flex-wrap items-center gap-2 mt-1 justify-center md:justify-start">
                <span className="inline-flex items-center px-2 sm:px-3 py-1 
                                 rounded-full text-xs sm:text-sm font-medium 
                                 bg-purple-100 text-purple-800">
                  {currentUser.Role || 'User'}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full 
                                  text-xs sm:text-sm font-medium 
                                  ${currentUser.IsActive ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
                  {currentUser.IsActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Summary */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 md:grid-cols-${isAdmin ? '4' : '5'} gap-4 sm:gap-6 mb-6 md:mb-10 animate-fade-in-up`}>
          {stats.map((stat, idx) => (
            <div key={stat.label} 
                 className={`p-4 sm:p-6 rounded-2xl shadow-lg bg-gradient-to-br 
                             ${stat.color} text-white flex flex-col items-center 
                             transform hover:scale-105 transition-all duration-300`}>
              <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 drop-shadow-lg">
                {rfqLoading && !isAdmin ? '-' : stat.value}
              </div>
              <div className="text-xs sm:text-sm opacity-90 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
        
        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 md:mb-8 animate-fade-in-up">
          {/* Area Chart Card (Replaced Bar Chart) */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center hover:shadow-2xl transition-all duration-300">
            <h2 className="text-lg sm:text-xl font-semibold text-[#2b6777] mb-4">
              {isAdmin ? "Monthly Overview" : "RFQs by Month"}
            </h2>
            
            {!isAdmin && (!areaData || areaData.length === 0) ? (
              // Show message if Vendoruser has no RFQs
              <div className="flex items-center justify-center w-full h-[200px] text-gray-500">
                {rfqLoading ? "Loading RFQs..." : "No RFQ data available"}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={isAdmin ? adminAreaData : areaData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2b6777" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2b6777" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey={isAdmin ? "value" : "rfqs"} 
                    name={isAdmin ? "Activity" : "RFQs"}
                    stroke="#2b6777" 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Line Chart Card */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center hover:shadow-2xl transition-all duration-300">
            <h2 className="text-lg sm:text-xl font-semibold text-[#2b6777] mb-4">
              {isAdmin ? 'Weekly Activity' : 'RFQs by Day of Week'}
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={isAdmin ? adminLineData : lineData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={isAdmin ? "value" : "rfqs"} 
                  name={isAdmin ? "Activity" : "RFQs"}
                  stroke="#52ab98" 
                  strokeWidth={3} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Pie Chart Card */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center hover:shadow-2xl transition-all duration-300">
            <h2 className="text-lg sm:text-xl font-semibold text-[#2b6777] mb-4">
              {isAdmin ? 'Distribution' : 'RFQs by Status'}
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie 
                  data={isAdmin ? adminPieData : pieData} 
                  dataKey="value" 
                  nameKey="name" 
                  cx="50%" 
                  cy="50%" 
                  outerRadius={60} 
                  label
                >
                  {(isAdmin ? adminPieData : pieData).map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={isAdmin ? adminPieColors[index % adminPieColors.length] : pieColors[index % pieColors.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* RFQ List Section */}
        <RFQDashboard />
        
        {/* Purchase Order Section - Only for Admin */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 md:mb-8 overflow-x-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6">Purchase Orders</h2>
            <table className="w-full min-w-[600px] bg-white rounded-2xl shadow-lg border-separate border-spacing-0 text-xs sm:text-sm">
              <thead>
                <tr>
                  {['PON','PO Date','Current Status','Shipped Status','Recept Status','Overdue Status','Due'].map((th, i) => (
                    <th key={i} className="bg-[#AAD7D9] text-[#2b6777] px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-center border-r border-white font-bold first:rounded-tl-2xl last:rounded-tr-2xl">
                      {th}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
            <h1 className="text-center text-[#2b6777] p-4 sm:p-6">Currently not available</h1>
          </div>
        
        {/* PR Details Modal - Only for Admin */}
        {isAdmin && showPRModal && selectedPR && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-[#2b6777]">PR Details - {selectedPR.prNo}</h3>
                <button
                  onClick={() => setShowPRModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PR Number</label>
                    <p className="text-base sm:text-lg font-semibold text-[#2b6777]">{selectedPR.prNo}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PR Date</label>
                    <p className="text-base sm:text-lg text-[#2b6777]">{selectedPR.prDate}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedPR.prStatus)}`}>
                      {selectedPR.prStatus}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Submission Date</label>
                    <p className="text-base sm:text-lg text-[#2b6777]">{selectedPR.submissionDate}</p>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-[#2b6777] mb-3">Additional Details</h4>
                  <p className="text-gray-600">
                    This is a sample purchase requisition with detailed information. 
                    The requisition was submitted for approval and contains all necessary 
                    documentation and specifications for the requested items.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* PO Details Modal - Only for Admin */}
        {isAdmin && showPOModal && selectedPO && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-[#2b6777]">PO Details - {selectedPO.pon}</h3>
                <button
                  onClick={() => setShowPOModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>
              {/* Horizontal scroll wrapper */}
              <div className="overflow-x-auto">
                <div className="min-w-[600px] space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                      <p className="text-base sm:text-lg font-semibold text-[#2b6777]">{selectedPO.pon}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PO Date</label>
                      <p className="textbase sm:text-lg text-[#2b6777]">{selectedPO.poDate}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedPO.currentStatus)}`}>
                        {selectedPO.currentStatus}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <p className="text-base sm:text-lg text-[#2b6777]">{selectedPO.due}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Shipped Status</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedPO.shippedStatus)}`}>
                        {selectedPO.shippedStatus}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reception Status</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedPO.receptStatus)}`}>
                        {selectedPO.receptStatus}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Overdue Status</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedPO.overdueStatus)}`}>
                        {selectedPO.overdueStatus}
                      </span>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-[#2b6777] mb-3">Order Summary</h4>
                    <p className="text-gray-600">
                      This purchase order contains detailed information about the procurement process, 
                      including shipping details, delivery status, and payment terms. The order is 
                      being tracked through various stages from creation to final delivery.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Custom CSS for animations */}
        <style jsx>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes fade-in-up {
            from { transform: translateY(50px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.7s ease-out;
          }
          .animate-fade-in-up {
            animation: fade-in-up 1s ease-out;
          }
        `}</style>
      </main>
    </div>
  );
};

export default Dashboard;